import { useContext, useState, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { MaterialContext } from "../context/MaterialContext";

import navBarStyle from "../styles/nav.module.scss";
import FilterBox from "./FilterBox";
import UserBox from "./UserBox";
import {
  downArrowIcon,
  designerIcon,
  vendorIcon,
  guestIcon,
  closeIcon,
} from "./Icons";
import SearchBar from "./SearchBar";

function NavBar() {
  const { collection, userInformation, changelogOutUser, media } =
    useContext(UserContext);
  const { isFilter, refetch } = useContext(MaterialContext);
  const [userMenuIsVisible, setUserMenuIsVisible] = useState(false);
  const [filterMenuIsVisible, setFilterMenuIsVisible] = useState(false);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isSingleMaterialRoute =
    /^\/[^/]+\/[^/]+$/.test(location.pathname) &&
    !location.pathname.startsWith("/material/");
  const isImmersiveMobileRoute =
    location.pathname === "/help" || isSingleMaterialRoute;

  const userType = (type) => {
    if (type === "vendor") {
      return vendorIcon;
    }
    if (type === "designer") {
      return designerIcon;
    }
    if (type === "guest") {
      return guestIcon;
    }
  };

  useEffect(() => {
    if (location.pathname !== "/library") {
      setUserMenuIsVisible(false);
      setFilterMenuIsVisible(false);
    }
    setMobileActionsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (media !== "mobile") {
      document.body.style.overflow = "";
      setMobileNavHidden(false);
      return;
    }

    if (isImmersiveMobileRoute) {
      setMobileNavHidden(false);
      return;
    }

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (mobileActionsOpen) {
        setMobileNavHidden(false);
        lastScrollY = currentScrollY;
        return;
      }

      if (currentScrollY <= 30) {
        setMobileNavHidden(false);
        lastScrollY = currentScrollY;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollY;

      if (scrollDelta > 8) {
        setMobileNavHidden(true);
      } else if (scrollDelta < -8) {
        setMobileNavHidden(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [media, isImmersiveMobileRoute, mobileActionsOpen]);

  useEffect(() => {
    if (media === "mobile") {
      document.body.style.overflow = mobileActionsOpen ? "hidden" : "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [media, mobileActionsOpen]);

  const handleClickAction = (navigateTo) => {
    navigate(navigateTo);
  };

  const handleDropDownMenu = (type) => {
    if (type === "user") {
      setUserMenuIsVisible(!userMenuIsVisible);
      setFilterMenuIsVisible(false);
    }
    if (type === "filter") {
      setUserMenuIsVisible(false);
      setFilterMenuIsVisible(!filterMenuIsVisible);
    }
  };

  const handleMobileBack = () => {
    navigate("/library");
  };

  const handleMobileNavigate = (path) => {
    navigate(path);
    setMobileActionsOpen(false);
  };

  const handleMobileMenuToggle = (type) => {
    setMobileActionsOpen(false);
    handleDropDownMenu(type);
  };

  if (media === "mobile") {
    return (
      <>
        <FilterBox
          filterMenuIsVisible={filterMenuIsVisible}
          setFilterMenuIsVisible={setFilterMenuIsVisible}
        />
        <UserBox
          userInformation={userInformation}
          changelogOutUser={changelogOutUser}
          userMenuIsVisible={userMenuIsVisible}
          setUserMenuIsVisible={setUserMenuIsVisible}
          refetch={refetch}
        />
        {isImmersiveMobileRoute ? (
          <div className={`${navBarStyle.bar} ${navBarStyle.mobileMinimalBar}`}>
            <button
              className={navBarStyle.mobileBackButton}
              onClick={handleMobileBack}
            >
              Back
            </button>
            <div className={navBarStyle.mobileMinimalTitle}>MATLIB</div>
          </div>
        ) : (
          <>
            <div
              className={`${navBarStyle.bar} ${navBarStyle.mobileBar} ${
                mobileNavHidden ? navBarStyle.mobileBarHidden : ""
              }`}
            >
              <div
                onClick={() => handleClickAction("library")}
                className={navBarStyle.logo}
              >
                MATLIB
              </div>

              <div className={navBarStyle.mobileNavigation}>
                <NavLink
                  className={({ isActive }) =>
                    isActive ? navBarStyle.mobileActiveLink : ""
                  }
                  to={`library`}
                >
                  Library
                </NavLink>
                <NavLink
                  className={({ isActive }) =>
                    isActive ? navBarStyle.mobileActiveLink : ""
                  }
                  to={`collection`}
                >
                  Collection
                  <span className={navBarStyle.mobileCollectionCount}>
                    {collection?.length}
                  </span>
                </NavLink>
                <button
                  className={`${navBarStyle.mobileActionButton} ${
                    mobileActionsOpen ? navBarStyle.active : ""
                  }`}
                  onClick={() => setMobileActionsOpen((value) => !value)}
                >
                  Menu
                  {downArrowIcon}
                </button>
              </div>
            </div>

            <div
              className={`${navBarStyle.mobilePanelOverlay} ${
                mobileActionsOpen ? "" : navBarStyle.mobilePanelOverlayHidden
              }`}
              onClick={() => setMobileActionsOpen(false)}
            ></div>

            <div
              className={`${navBarStyle.mobileActionPanel} ${
                mobileActionsOpen ? "" : navBarStyle.mobilePanelHidden
              }`}
            >
              <div className={navBarStyle.mobilePanelHeader}>
                <div className={navBarStyle.mobilePanelTitle}>Menu</div>
                <button
                  className={navBarStyle.mobilePanelClose}
                  onClick={() => setMobileActionsOpen(false)}
                >
                  {closeIcon}
                </button>
              </div>

              <div className={navBarStyle.mobilePanelRole}>
                {userType(userInformation.userType)}
                <span>{userInformation.userType || "guest"}</span>
              </div>

              <SearchBar />

              <div className={navBarStyle.mobileDrawerNav}>
                <button
                  className={navBarStyle.mobilePanelButton}
                  onClick={() => handleMobileNavigate("/library")}
                >
                  Library
                </button>
                <button
                  className={navBarStyle.mobilePanelButton}
                  onClick={() => handleMobileNavigate("/collection")}
                >
                  Collection ({collection?.length || 0})
                </button>
              </div>

              <div className={navBarStyle.mobileActionRow}>
                {location.pathname === "/library" && (
                  <button
                    className={`${navBarStyle.mobilePanelButton} ${
                      filterMenuIsVisible ? navBarStyle.active : ""
                    }`}
                    onClick={() => handleMobileMenuToggle("filter")}
                  >
                    {isFilter ? "Filters: On" : "Filters"}
                  </button>
                )}
                <button
                  className={`${navBarStyle.mobilePanelButton} ${
                    userMenuIsVisible ? navBarStyle.active : ""
                  }`}
                  onClick={() => handleMobileMenuToggle("user")}
                >
                  Account
                </button>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <FilterBox
        filterMenuIsVisible={filterMenuIsVisible}
        setFilterMenuIsVisible={setFilterMenuIsVisible}
      />
      <UserBox
        userInformation={userInformation}
        changelogOutUser={changelogOutUser}
        userMenuIsVisible={userMenuIsVisible}
        setUserMenuIsVisible={setUserMenuIsVisible}
        refetch={refetch}
      />
      <div className={navBarStyle.bar}>
        <div
          onClick={() => handleClickAction("library")}
          className={navBarStyle.logo}
        >
          MATLIB
        </div>
        <SearchBar />
        <div className={navBarStyle.navigation}>
          {location.pathname === "/library" && (
            <div
              className={
                !filterMenuIsVisible
                  ? `${navBarStyle.filter}`
                  : `${navBarStyle.filter} ${navBarStyle.active}`
              }
              onClick={() => handleDropDownMenu("filter")}
            >
              {isFilter ? <div>FILTERED</div> : <div>FILTER</div>}
              {downArrowIcon}
              {/* {search.length >= 1 && isFilter ? (
                <span
                  style={{
                    position: "absolute",
                    bottom: "18px",
                    fontSize: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  More options available
                </span>
              ) : null} */}
            </div>
          )}
          <NavLink
            className={({ isActive }) => (isActive ? "active" : "inactive")}
            to={`library`}
          >
            LIBRARY
          </NavLink>
          <NavLink
            className={({ isActive }) => (isActive ? "active" : "inactive")}
            to={`collection`}
          >
            COLLECTION{" "}
            <span className={navBarStyle.collectionCount}>
              {collection?.length}
            </span>
          </NavLink>
          <div
            className={
              !userMenuIsVisible
                ? `${navBarStyle.userMenu}`
                : `${navBarStyle.userMenu} ${navBarStyle.active}`
            }
            onClick={() => handleDropDownMenu("user")}
          >
            {userType(userInformation.userType)}
          </div>
        </div>
      </div>
    </>
  );
}

export default NavBar;
